import { apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "./client";

export type OrgSettings = {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  currencyCode: string;
  taxPriceMode: "inclusive" | "exclusive";
  taxRatePct: string;
  discountSoftCapPct: number;
  /** Default true — when false, stock-out below on-hand is blocked */
  allowOversell: boolean;
  /** null = unlimited backdating — plan/35 */
  docDateMaxBackDays?: number | null;
  docDateBackdatePolicy?: "soft" | "hard";
  /** When true, collect against SI not raw SO — plan/41 */
  requireSalesInvoices?: boolean;
  /** When true, pay against PI not raw PO — plan/41 */
  requirePurchaseInvoices?: boolean;
  /** Print / letterhead — plan/43 */
  legalName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  countryCode?: string;
  tin?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  registrationNote?: string | null;
  permitNote?: string | null;
  hasLogo?: boolean;
};

export type OrgRole = {
  id: string;
  code: string;
  name: string;
  isSystem: boolean;
  permissions?: string[];
};

export type PermissionCatalogItem = {
  key: string;
  label: string;
  group: "action" | "override" | "meta";
};

export type OrgMember = {
  userId: string;
  email: string | null;
  displayName: string | null;
  userCode: string | null;
  hasPasscode: boolean;
  pendingInvite: boolean;
  deactivatedAt: string | null;
  roles: Array<{ id: string; code: string; name: string }>;
  /** User-level denies that beat role allows — plan/38 §6 */
  excepts?: string[];
};

export function getOrgSettings(companyRef: string) {
  return apiGet<{ org: OrgSettings }>(`/c/${companyRef}/settings/org`);
}

export function patchOrgSettings(
  companyRef: string,
  body: {
    taxPriceMode?: "inclusive" | "exclusive";
    taxRatePct?: number | string;
    discountSoftCapPct?: number;
    allowOversell?: boolean;
    docDateMaxBackDays?: number | null;
    docDateBackdatePolicy?: "soft" | "hard";
    requireSalesInvoices?: boolean;
    requirePurchaseInvoices?: boolean;
    name?: string;
    timezone?: string;
    legalName?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    province?: string | null;
    postalCode?: string | null;
    countryCode?: string | null;
    tin?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    registrationNote?: string | null;
    permitNote?: string | null;
  },
) {
  return apiPatch<{ org: OrgSettings }>(
    `/c/${companyRef}/settings/org`,
    body,
  );
}

export function uploadOrgLogo(companyRef: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiUpload<{ hasLogo: boolean }>(
    `/c/${companyRef}/settings/org/logo`,
    form,
  );
}

export function removeOrgLogo(companyRef: string) {
  return apiDelete<{ hasLogo: boolean }>(
    `/c/${companyRef}/settings/org/logo`,
  );
}

export function listOrgRoles(companyRef: string) {
  return apiGet<{
    roles: OrgRole[];
    catalog: PermissionCatalogItem[];
  }>(`/c/${companyRef}/settings/roles`);
}

export function setRolePermissions(
  companyRef: string,
  roleId: string,
  permissions: string[],
) {
  return apiPut<{ role: OrgRole }>(
    `/c/${companyRef}/settings/roles/${roleId}/permissions`,
    { permissions },
  );
}

export function listOrgMembers(companyRef: string) {
  return apiGet<{
    members: OrgMember[];
    exceptable?: PermissionCatalogItem[];
  }>(`/c/${companyRef}/settings/members`);
}

export function inviteOrgMember(
  companyRef: string,
  body: {
    email?: string;
    roleId?: string;
    roleCode?: string;
    displayName?: string;
    userCode?: string;
    pin?: string;
  },
) {
  return apiPost<{
    member: {
      userId: string;
      email: string | null;
      displayName: string | null;
      pendingInvite: boolean;
      userCode: string | null;
      hasPasscode: boolean;
    };
  }>(`/c/${companyRef}/settings/members`, body);
}

export function setMemberCredentials(
  companyRef: string,
  userId: string,
  body: {
    userCode?: string;
    regenerateCode?: boolean;
    pin?: string;
    clearPin?: boolean;
    displayName?: string;
  },
) {
  return apiPut<{
    member: {
      userId: string;
      userCode: string | null;
      displayName?: string | null;
      hasPasscode: boolean;
    };
  }>(`/c/${companyRef}/settings/members/${userId}/credentials`, body);
}

export function setMemberRoles(
  companyRef: string,
  userId: string,
  roleIds: string[],
) {
  return apiPut<{
    member: {
      userId: string;
      roles: Array<{ id: string; code: string }>;
    };
  }>(`/c/${companyRef}/settings/members/${userId}/roles`, { roleIds });
}

export function setMemberExcepts(
  companyRef: string,
  userId: string,
  excepts: string[],
) {
  return apiPut<{
    member: { userId: string; excepts: string[] };
  }>(`/c/${companyRef}/settings/members/${userId}/excepts`, { excepts });
}

export function deactivateOrgMember(companyRef: string, userId: string) {
  return apiPost<{ member: { userId: string; deactivated: boolean } }>(
    `/c/${companyRef}/settings/members/${userId}/deactivate`,
  );
}

export function reactivateOrgMember(companyRef: string, userId: string) {
  return apiPost<{ member: { userId: string; deactivated: boolean } }>(
    `/c/${companyRef}/settings/members/${userId}/reactivate`,
  );
}
